/**
 * Wallet List Component
 * Display grid of wallet cards with search and filters
 * Modern redesign with glassmorphism effects
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWalletTrackerStore } from '../../store/walletTrackerStore';
import { WalletCard } from './WalletCard';
import { Icons } from '../Icons';
import { exportWalletsToCSV } from '../../utils/csvExport';
import { TRACKER_COLORS } from '../../config/monitorColors';

// ============================================
// CONSTANTS & STYLES
// ============================================

// Predefined tags for filtering
const FILTER_TAGS = [
  { id: 'smart-money', label: 'Smart Money', emoji: '🧠', color: '#8b5cf6' },
  { id: 'whale', label: 'Whale', emoji: '🐋', color: '#3b82f6' },
  { id: 'defi-pro', label: 'DeFi Pro', emoji: '🏦', color: '#10b981' },
  { id: 'nft-trader', label: 'NFT Trader', emoji: '🎨', color: '#ec4899' },
  { id: 'influencer', label: 'Influencer', emoji: '⭐', color: '#f59e0b' },
  { id: 'fund', label: 'Fund', emoji: '💼', color: '#6366f1' },
  { id: 'personal', label: 'Personal', emoji: '👤', color: '#64748b' },
];

const s = {
  searchContainer: {
    padding: '20px',
    background: 'rgba(0, 0, 0, 0.2)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
  },
  searchInput: {
    width: '100%',
    padding: '12px 14px 12px 42px',
    fontSize: '14px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '10px',
    background: 'rgba(0, 0, 0, 0.3)',
    color: 'var(--text)',
    outline: 'none',
    transition: 'all 0.2s',
  },
  tagButton: (isActive: boolean, color: string) => ({
    fontSize: '12px',
    padding: '6px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    borderRadius: '20px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s',
    background: isActive ? `${color}25` : 'rgba(255, 255, 255, 0.05)',
    color: isActive ? color : 'var(--muted)',
    fontWeight: isActive ? 600 : 400,
  }),
  filterBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 20px',
    background: 'rgba(0, 0, 0, 0.15)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
    fontSize: '13px',
  },
  select: {
    fontSize: '12px',
    padding: '6px 10px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '6px',
    background: 'rgba(0, 0, 0, 0.3)',
    color: 'var(--text)',
    cursor: 'pointer',
  },
  gridContainer: {
    padding: '20px',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '16px',
  },
  emptyState: {
    padding: '60px 40px',
    textAlign: 'center' as const,
  },
};

interface WalletListProps {
  onSelectWallet?: (address: string) => void;
}

export function WalletList({ onSelectWallet }: WalletListProps) {
  const { t } = useTranslation();
  const { wallets, removeWallet, filter, setFilter } = useWalletTrackerStore();
  const [searchQuery, setSearchQuery] = useState(filter.searchQuery || '');

  // Handle search
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setFilter({ searchQuery: query });
  };

  // Toggle tag filter
  const toggleTagFilter = (tagId: string) => {
    const newTags = filter.tags.includes(tagId)
      ? filter.tags.filter(t => t !== tagId)
      : [...filter.tags, tagId];
    setFilter({ tags: newTags });
  };

  // Get filtered wallets
  const filteredWallets = wallets.filter(wallet => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        wallet.label.toLowerCase().includes(query) ||
        wallet.address.toLowerCase().includes(query) ||
        wallet.ensName?.toLowerCase().includes(query) ||
        wallet.tags.some(tag => tag.toLowerCase().includes(query));

      if (!matchesSearch) return false;
    }

    // Tag filter
    if (filter.tags.length > 0) {
      const matchesTags = filter.tags.some(tag => wallet.tags.includes(tag));
      if (!matchesTags) return false;
    }

    return true;
  });

  // Sort wallets
  const sortedWallets = [...filteredWallets].sort((a, b) => {
    switch (filter.sortBy) {
      case 'name':
        return filter.sortOrder === 'asc'
          ? a.label.localeCompare(b.label)
          : b.label.localeCompare(a.label);
      case 'added':
        const dateA = new Date(a.addedAt).getTime();
        const dateB = new Date(b.addedAt).getTime();
        return filter.sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      default:
        return 0;
    }
  });

  return (
    <div>
      {/* Search Bar */}
      <div style={s.searchContainer}>
        <div style={{ position: 'relative', marginBottom: '16px' }}>
          <Icons.Search
            size={18}
            style={{
              position: 'absolute',
              left: '14px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--muted)',
              opacity: 0.6,
            }}
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder={t('walletTracker.search.placeholder', 'Search by name, address, ENS, or tags...')}
            style={s.searchInput}
          />
          {searchQuery && (
            <button
              onClick={() => handleSearch('')}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                color: 'var(--muted)',
                cursor: 'pointer',
                padding: '4px',
              }}
            >
              <Icons.X size={16} />
            </button>
          )}
        </div>

        {/* Tag Filters */}
        <div>
          <div style={{
            fontSize: '11px',
            fontWeight: 600,
            marginBottom: '10px',
            color: 'var(--muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>
            {t('walletTracker.filter.byTag', 'Filter by Tag')}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {FILTER_TAGS.map(tag => {
              const isActive = filter.tags.includes(tag.id);
              const count = wallets.filter(w => w.tags.includes(tag.id)).length;

              return (
                <button
                  key={tag.id}
                  onClick={() => toggleTagFilter(tag.id)}
                  style={{
                    ...s.tagButton(isActive, tag.color),
                    opacity: count === 0 ? 0.4 : 1,
                    cursor: count === 0 ? 'not-allowed' : 'pointer',
                  }}
                  disabled={count === 0}
                >
                  <span>{tag.emoji}</span>
                  <span>{tag.label}</span>
                  <span style={{
                    fontSize: '10px',
                    padding: '2px 6px',
                    borderRadius: '10px',
                    background: isActive ? `${tag.color}40` : 'rgba(255, 255, 255, 0.1)',
                    marginLeft: '2px',
                  }}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div style={s.filterBar}>
        <div style={{ color: 'var(--muted)' }}>
          <span style={{ fontWeight: 600, color: TRACKER_COLORS.primary }}>{sortedWallets.length}</span>
          {' '}{t('walletTracker.filter.walletsFound', 'wallet(s) found')}
          {filter.tags.length > 0 && (
            <span style={{ marginLeft: '8px', opacity: 0.7 }}>
              ({t('walletTracker.filter.filtered', 'filtered')})
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button
            onClick={() => exportWalletsToCSV(sortedWallets)}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '6px',
              color: 'var(--text)',
              cursor: 'pointer',
            }}
            title={t('walletTracker.export', 'Export to CSV')}
          >
            <Icons.Download size={14} />
            {t('common.export', 'Export')}
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
              {t('walletTracker.sort.sortBy', 'Sort:')}
            </span>
            <select
              aria-label={t('walletTracker.sort.sortBy', 'Sort:')}
              value={filter.sortBy}
              onChange={(e) => setFilter({ sortBy: e.target.value as 'name' | 'added' })}
              style={s.select}
            >
              <option value="added">{t('walletTracker.sort.dateAdded', 'Date Added')}</option>
              <option value="name">{t('walletTracker.sort.name', 'Name')}</option>
            </select>
            <button
              onClick={() => setFilter({ sortOrder: filter.sortOrder === 'asc' ? 'desc' : 'asc' })}
              style={{
                padding: '6px 10px',
                fontSize: '14px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '6px',
                color: 'var(--text)',
                cursor: 'pointer',
              }}
              title={filter.sortOrder === 'asc' ? t('walletTracker.sort.descending', 'Sort descending') : t('walletTracker.sort.ascending', 'Sort ascending')}
            >
              {filter.sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>
      </div>

      {/* Wallet Grid */}
      {sortedWallets.length > 0 ? (
        <div style={s.gridContainer}>
          {sortedWallets.map(wallet => (
            <WalletCard
              key={wallet.address}
              wallet={wallet}
              onSelect={() => onSelectWallet?.(wallet.address)}
              onDelete={() => removeWallet(wallet.address)}
            />
          ))}
        </div>
      ) : (
        /* Empty state for filtered results */
        <div style={s.emptyState}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '16px',
            background: 'rgba(6, 182, 212, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
          }}>
            <Icons.Search size={28} style={{ color: TRACKER_COLORS.primary, opacity: 0.5 }} />
          </div>
          <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>
            {t('walletTracker.empty.noWalletsFound', 'No wallets found')}
          </h3>
          <p style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '20px' }}>
            {t('walletTracker.empty.tryAdjusting', 'Try adjusting your search or filters')}
          </p>
          <button
            onClick={() => {
              setSearchQuery('');
              setFilter({ searchQuery: '', tags: [] });
            }}
            style={{
              padding: '10px 20px',
              fontSize: '13px',
              fontWeight: 600,
              background: `linear-gradient(135deg, ${TRACKER_COLORS.primary}, ${TRACKER_COLORS.secondary})`,
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            {t('walletTracker.filter.clearFilters', 'Clear Filters')}
          </button>
        </div>
      )}
    </div>
  );
}
